import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  isServerFrontDataLabelOverlayPath,
  isServerLabelOverlayPath,
  isServerVolumeLabelPath,
  type ImageLayerKey,
  type ResolvedImageLayerPaths,
} from "./cprImageResources";
import {
  clearServerImageCacheExceptRevision,
  getServerMemoryImageData,
  isServerMemoryImageSource,
  prefetchImageSourcesQueued,
  retainServerImageCacheScope,
  resolveImageSourceQueued,
  type ImageCacheScope,
} from "./serverImageCache";
import {
  getLocalOverlayImageContextVersion,
  subscribeLocalOverlayImageContextVersion,
  type OverlayPaintPolicy,
} from "./localOverlayImageProvider";
import {
  calculateWindowLevelWidthFromDrag,
  type ImageWindowingPolicy,
} from "./imageWindowing";
import {
  calculateImageFitRect,
  calculateZoomedViewportTransform,
  type ImageFitRect,
  type ImageFitMode,
  type ImageViewBox,
  type ViewportTransform,
} from "./cprImageLayout";

export type ImageLayerStackViewportContext = {
  imageWidth: number;
  imageHeight: number;
  containerWidth: number;
  containerHeight: number;
  fitRect: ImageFitRect;
  viewportTransform: ViewportTransform;
  stageRect: ImageFitRect;
  scaleX: number;
  scaleY: number;
  imageToViewportPoint: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  };
  viewportToImagePoint: (point: { x: number; y: number }) => {
    x: number;
    y: number;
  };
};

export type ImageLayerStackWheelEvent = React.WheelEvent<HTMLDivElement> | WheelEvent;

type ImageLayerStackProps = {
  layers: ResolvedImageLayerPaths | null;
  revision?: string;
  width: number;
  height: number;
  seriesId?: string;
  accessToken?: string;
  prefetchLayers?: Array<ResolvedImageLayerPaths | null>;
  prefetchConcurrency?: number;
  className?: string;
  fitMode?: ImageFitMode;
  initialViewBox?: ImageViewBox;
  viewportAnchorPoint?: { x: number; y: number };
  resetKey?: string;
  enableViewportTransform?: boolean;
  activeToolId?: string;
  visibility?: Partial<Record<ImageLayerKey, boolean>>;
  overlayPolicy?: OverlayPaintPolicy;
  windowingPolicy?: ImageWindowingPolicy;
  cacheScope?: ImageCacheScope;
  diagnosticLabel?: string;
  priority?: number;
  loading?: boolean;
  disableStaleFallback?: boolean;
  onWheel?: (event: ImageLayerStackWheelEvent) => void;
  onScrollDrag?: (deltaX: number, deltaY: number) => void;
  onWindowLevelChange?: (next: ImageWindowingPolicy) => void;
  viewportOverlay?: (
    context: ImageLayerStackViewportContext,
  ) => React.ReactNode;
  children?: React.ReactNode;
};

type DragInteractionKind = "pan" | "zoom" | "scroll" | "window-level";
type ImageLayerResolveStatus =
  | "pending"
  | "resolved"
  | "failed"
  | "transparent"
  | "hidden";
type ImageLayerState = {
  key: ImageLayerKey;
  source?: string;
  src?: string;
  status: ImageLayerResolveStatus;
  error?: string;
  stale?: boolean;
  staleSource?: string;
};

type DisplayLayerEntry = {
  key: ImageLayerKey;
  src: string;
  source?: string;
};

type DisplayGeometry = {
  width: number;
  height: number;
  initialViewBox?: ImageViewBox;
  viewportAnchorPoint?: { x: number; y: number };
};

type ViewportLayoutSnapshot = {
  resetKey?: string;
  revision?: string;
  fitMode: ImageFitMode;
  containerWidth: number;
  containerHeight: number;
  imageWidth: number;
  imageHeight: number;
  initialViewBox?: ImageViewBox;
  viewportAnchorPoint?: { x: number; y: number };
  fitRect: ImageFitRect;
};

const IMAGE_LAYER_RENDER_ORDER: ImageLayerKey[] = [
  "base",
  "lumen",
  "lumenoutline",
  "plaque",
  "plaqueoutline",
  "fai",
  "aorta",
  "avc",
];

const SERVER_IMAGE_STACK_PERF_LOG_ENABLED = false;
const CPR_WHEEL_ACTIVE_MS = 140;
const CPR_PREFETCH_IDLE_MS = 0;
const CPR_PREFETCH_FRAME_LIMIT = Number.MAX_SAFE_INTEGER;
const CPR_PREFETCH_PATH_LIMIT = 768;
const CPR_LAYER_RESOLVE_RETRY_DELAYS_MS = [80, 180, 360];

function stackPerfNow() {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function stackRoundMs(value: number) {
  return Math.round(value * 100) / 100;
}


function logStackPerf(_event: string, _payload: Record<string, unknown>) {
  // Performance logging disabled for final marker sync cleanup.
}

type ServerMemoryCanvasLayerProps = {
  layerKey: ImageLayerKey;
  src: string;
  onRendered?: (width: number, height: number, src: string) => void;
};

function ServerMemoryCanvasLayer({
  layerKey,
  src,
  onRendered,
}: ServerMemoryCanvasLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useLayoutEffect(() => {
    const totalStart = stackPerfNow();
    const canvas = canvasRef.current;
    const lookupStart = stackPerfNow();
    const image = getServerMemoryImageData(src);
    const lookupMs = stackPerfNow() - lookupStart;
    if (!canvas || !image) return;
    const resizeStart = stackPerfNow();
    if (canvas.width !== image.width) canvas.width = image.width;
    if (canvas.height !== image.height) canvas.height = image.height;
    const resizeMs = stackPerfNow() - resizeStart;
    const contextStart = stackPerfNow();
    const ctx = canvas.getContext("2d", { alpha: true });
    const contextMs = stackPerfNow() - contextStart;
    if (!ctx) return;
    const imageDataStart = stackPerfNow();
    const imageData = new ImageData(image.rgba, image.width, image.height);
    const imageDataMs = stackPerfNow() - imageDataStart;
    const putStart = stackPerfNow();
    ctx.putImageData(imageData, 0, 0);
    const putMs = stackPerfNow() - putStart;
    logStackPerf("memory-canvas-render", {
      layer: layerKey,
      width: image.width,
      height: image.height,
      rgbaBytes: image.rgba.byteLength,
      lookupMs: stackRoundMs(lookupMs),
      resizeMs: stackRoundMs(resizeMs),
      contextMs: stackRoundMs(contextMs),
      imageDataMs: stackRoundMs(imageDataMs),
      putMs: stackRoundMs(putMs),
      totalMs: stackRoundMs(stackPerfNow() - totalStart),
    });
    onRendered?.(image.width, image.height, src);
  }, [layerKey, onRendered, src]);

  return (
    <canvas
      ref={canvasRef}
      className={`server-image-layer server-image-layer-${layerKey}`}
      data-layer-key={layerKey}
      data-layer-status="resolved"
      data-memory-image="1"
      style={{ transition: "none" }}
    />
  );
}


type ServerMemoryCompositeCanvasLayerProps = {
  entries: Array<readonly [ImageLayerKey, string]>;
  onRendered?: (width: number, height: number, src: string) => void;
};

type ServerMemoryImageData = NonNullable<
  ReturnType<typeof getServerMemoryImageData>
>;

const stackIsLittleEndian = (() => {
  const buffer = new ArrayBuffer(4);
  new Uint32Array(buffer)[0] = 0x0a0b0c0d;
  return new Uint8Array(buffer)[0] === 0x0d;
})();

function blendByteChannel(src: number, dst: number, alpha: number) {
  return Math.floor((src * alpha + dst * (255 - alpha) + 127) / 255);
}

function blendOverlayRgbaInPlace(
  base: Uint8ClampedArray,
  overlay: Uint8ClampedArray,
) {
  const byteLength = Math.min(base.byteLength, overlay.byteLength);
  if (byteLength <= 0) return;

  if (
    stackIsLittleEndian &&
    base.byteOffset % 4 === 0 &&
    overlay.byteOffset % 4 === 0
  ) {
    const pixelCount = Math.floor(byteLength / 4);
    const basePixels = new Uint32Array(base.buffer, base.byteOffset, pixelCount);
    const overlayPixels = new Uint32Array(
      overlay.buffer,
      overlay.byteOffset,
      pixelCount,
    );
    for (let i = 0; i < pixelCount; i += 1) {
      const src = overlayPixels[i];
      const alpha = src >>> 24;
      if (alpha === 0) continue;
      if (alpha === 255) {
        basePixels[i] = src;
        continue;
      }

      const dst = basePixels[i];
      const srcR = src & 0xff;
      const srcG = (src >>> 8) & 0xff;
      const srcB = (src >>> 16) & 0xff;
      const dstR = dst & 0xff;
      const dstG = (dst >>> 8) & 0xff;
      const dstB = (dst >>> 16) & 0xff;
      basePixels[i] =
        0xff000000 |
        (blendByteChannel(srcB, dstB, alpha) << 16) |
        (blendByteChannel(srcG, dstG, alpha) << 8) |
        blendByteChannel(srcR, dstR, alpha);
    }
    return;
  }

  for (let offset = 0; offset + 3 < byteLength; offset += 4) {
    const alpha = overlay[offset + 3];
    if (alpha === 0) continue;
    if (alpha === 255) {
      base[offset] = overlay[offset];
      base[offset + 1] = overlay[offset + 1];
      base[offset + 2] = overlay[offset + 2];
      base[offset + 3] = 255;
      continue;
    }
    base[offset] = blendByteChannel(overlay[offset], base[offset], alpha);
    base[offset + 1] = blendByteChannel(
      overlay[offset + 1],
      base[offset + 1],
      alpha,
    );
    base[offset + 2] = blendByteChannel(
      overlay[offset + 2],
      base[offset + 2],
      alpha,
    );
    base[offset + 3] = 255;
  }
}

function getCompositeMemoryImages(
  entries: Array<readonly [ImageLayerKey, string]>,
) {
  if (!entries.length) return null;
  const images = entries.map(([key, src]) => ({
    key,
    src,
    image: getServerMemoryImageData(src),
  }));
  const baseEntry = images.find((entry) => entry.key === "base") ?? images[0];
  const baseImage = baseEntry?.image;
  if (!baseImage) return null;
  const sameGeometry = images.every(
    (entry) =>
      entry.image &&
      entry.image.width === baseImage.width &&
      entry.image.height === baseImage.height,
  );
  return sameGeometry
    ? (images as Array<{
        key: ImageLayerKey;
        src: string;
        image: ServerMemoryImageData;
      }>)
    : null;
}

function canUseMemoryComposite(entries: Array<readonly [ImageLayerKey, string]>) {
  if (entries.length < 2) return false;
  if (!entries.every(([_key, src]) => isServerMemoryImageSource(src))) {
    return false;
  }
  return !!getCompositeMemoryImages(entries);
}

function ServerMemoryCompositeCanvasLayer({
  entries,
  onRendered,
}: ServerMemoryCompositeCanvasLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onRenderedRef = useRef(onRendered);

  useEffect(() => {
    onRenderedRef.current = onRendered;
  }, [onRendered]);

  const lastRenderedSignatureRef = useRef("");

  useLayoutEffect(() => {
    const srcSignature = entries.map(([key, src]) => `${key}:${src}`).join("|");
    if (lastRenderedSignatureRef.current === srcSignature) return;

    const totalStart = stackPerfNow();
    const canvas = canvasRef.current;
    const lookupStart = stackPerfNow();
    const images = getCompositeMemoryImages(entries);
    const lookupMs = stackPerfNow() - lookupStart;
    if (!canvas || !images?.length) return;

    const baseImage = images.find((entry) => entry.key === "base")?.image;
    if (!baseImage) return;

    const resizeStart = stackPerfNow();
    if (canvas.width !== baseImage.width) canvas.width = baseImage.width;
    if (canvas.height !== baseImage.height) canvas.height = baseImage.height;
    const resizeMs = stackPerfNow() - resizeStart;

    const contextStart = stackPerfNow();
    const ctx = canvas.getContext("2d", { alpha: true });
    const contextMs = stackPerfNow() - contextStart;
    if (!ctx) return;

    const composeStart = stackPerfNow();
    const composed = new Uint8ClampedArray(baseImage.rgba);
    images.forEach((entry) => {
      if (entry.key === "base") return;
      blendOverlayRgbaInPlace(composed, entry.image.rgba);
    });
    const composeMs = stackPerfNow() - composeStart;

    const imageDataStart = stackPerfNow();
    const imageData = new ImageData(composed, baseImage.width, baseImage.height);
    const imageDataMs = stackPerfNow() - imageDataStart;

    const putStart = stackPerfNow();
    ctx.putImageData(imageData, 0, 0);
    const putMs = stackPerfNow() - putStart;
    lastRenderedSignatureRef.current = srcSignature;

    logStackPerf("memory-composite-render", {
      layer: "composite",
      layerCount: images.length,
      width: baseImage.width,
      height: baseImage.height,
      rgbaBytes: composed.byteLength,
      lookupMs: stackRoundMs(lookupMs),
      resizeMs: stackRoundMs(resizeMs),
      contextMs: stackRoundMs(contextMs),
      composeMs: stackRoundMs(composeMs),
      imageDataMs: stackRoundMs(imageDataMs),
      putMs: stackRoundMs(putMs),
      totalMs: stackRoundMs(stackPerfNow() - totalStart),
    });
    onRenderedRef.current?.(baseImage.width, baseImage.height, srcSignature);
  }, [entries]);

  return (
    <canvas
      ref={canvasRef}
      className="server-image-layer server-image-layer-composite"
      data-layer-key="composite"
      data-layer-status="resolved"
      data-memory-image="composite"
      style={{ transition: "none" }}
    />
  );
}

function createInitialLayerStates(
  layers: ResolvedImageLayerPaths | null,
  visibility?: Partial<Record<ImageLayerKey, boolean>>,
): ImageLayerState[] {
  if (!layers?.base) return [];
  return IMAGE_LAYER_RENDER_ORDER.flatMap((key) => {
    const source = layers[key];
    if (!source) return [];
    const hidden = key !== "base" && visibility?.[key] === false;
    return [
      {
        key,
        source,
        status: hidden ? "hidden" : "pending",
      } as ImageLayerState,
    ];
  });
}

function isUnresolvedOverlayPseudoPath(path: string | undefined) {
  return (
    isServerLabelOverlayPath(path) ||
    isServerVolumeLabelPath(path) ||
    isServerFrontDataLabelOverlayPath(path)
  );
}

function sourceContinuityKey(source: string | undefined) {
  if (!source) return "";
  const [withoutQuery] = source.split("?");
  return withoutQuery
    .replace(/(hu|label|mapping|outline)%2Fangle[_-]?\d+\.[^/?#]+/gi, "$1%2Fangle")
    .replace(/(hu|label|mapping|outline)\/angle[_-]?\d+\.[^/?#]+/gi, "$1/angle")
    .replace(/angle[_-]?\d+/gi, "angle");
}

function canCarryStaleLayer(
  previous: ImageLayerState | undefined,
  next: ImageLayerState,
) {
  if (!previous?.src || !previous.source || !next.source) return false;
  return sourceContinuityKey(previous.source) === sourceContinuityKey(next.source);
}

function makeDisplayEntriesFromLayerStates(
  layerStates: ImageLayerState[],
  { includeStale = false }: { includeStale?: boolean } = {},
): DisplayLayerEntry[] {
  return layerStates
    .filter(
      (state): state is ImageLayerState & { src: string } =>
        state.status === "resolved" &&
        typeof state.src === "string" &&
        state.src.length > 0 &&
        (includeStale || !state.stale),
    )
    .map((state) => ({
      key: state.key,
      src: state.src,
      source: state.source,
    }));
}

function areDisplayEntriesEqual(
  a: DisplayLayerEntry[],
  b: DisplayLayerEntry[],
) {
  if (a.length !== b.length) return false;
  return a.every(
    (entry, index) =>
      entry.key === b[index]?.key &&
      entry.src === b[index]?.src &&
      entry.source === b[index]?.source,
  );
}

function areLayerStatesEqual(
  a: ImageLayerState[],
  b: ImageLayerState[],
) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((state, index) => {
    const other = b[index];
    return (
      state.key === other?.key &&
      state.source === other?.source &&
      state.src === other?.src &&
      state.status === other?.status &&
      state.error === other?.error &&
      state.stale === other?.stale &&
      state.staleSource === other?.staleSource
    );
  });
}

function areImageViewBoxesEqual(
  a: ImageViewBox | undefined,
  b: ImageViewBox | undefined,
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height
  );
}

function areDisplayGeometriesEqual(
  a: DisplayGeometry | undefined,
  b: DisplayGeometry | undefined,
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.width === b.width &&
    a.height === b.height &&
    areImageViewBoxesEqual(a.initialViewBox, b.initialViewBox) &&
    areImagePointsEqual(a.viewportAnchorPoint, b.viewportAnchorPoint)
  );
}

function areImageFitRectsEqual(
  a: ImageFitRect | undefined,
  b: ImageFitRect | undefined,
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height
  );
}

function areImagePointsEqual(
  a: { x: number; y: number } | undefined,
  b: { x: number; y: number } | undefined,
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y;
}

function isFiniteImagePoint(
  point: { x: number; y: number } | undefined,
): point is { x: number; y: number } {
  return !!point && Number.isFinite(point.x) && Number.isFinite(point.y);
}

function areViewportLayoutSnapshotsEqual(
  a: ViewportLayoutSnapshot | undefined,
  b: ViewportLayoutSnapshot | undefined,
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.resetKey === b.resetKey &&
    a.revision === b.revision &&
    a.fitMode === b.fitMode &&
    a.containerWidth === b.containerWidth &&
    a.containerHeight === b.containerHeight &&
    a.imageWidth === b.imageWidth &&
    a.imageHeight === b.imageHeight &&
    areImageViewBoxesEqual(a.initialViewBox, b.initialViewBox) &&
    areImagePointsEqual(a.viewportAnchorPoint, b.viewportAnchorPoint) &&
    areImageFitRectsEqual(a.fitRect, b.fitRect)
  );
}

function getViewportLayoutAnchor(layout: ViewportLayoutSnapshot) {
  if (isFiniteImagePoint(layout.viewportAnchorPoint)) {
    return {
      x: Math.max(0, Math.min(layout.imageWidth, layout.viewportAnchorPoint.x)),
      y: Math.max(0, Math.min(layout.imageHeight, layout.viewportAnchorPoint.y)),
    };
  }

  const viewBox = layout.initialViewBox;
  if (
    viewBox &&
    Number.isFinite(viewBox.x) &&
    Number.isFinite(viewBox.y) &&
    Number.isFinite(viewBox.width) &&
    Number.isFinite(viewBox.height) &&
    viewBox.width > 0 &&
    viewBox.height > 0
  ) {
    const x = Math.max(
      0,
      Math.min(layout.imageWidth, viewBox.x + viewBox.width * 0.5),
    );
    const y = Math.max(
      0,
      Math.min(layout.imageHeight, viewBox.y + viewBox.height * 0.5),
    );
    return { x, y };
  }

  return {
    x: layout.imageWidth * 0.5,
    y: layout.imageHeight * 0.5,
  };
}

function calculateAnchorCenteredViewportTransform({
  layout,
  scale = 1,
}: {
  layout: ViewportLayoutSnapshot;
  scale?: number;
}): ViewportTransform {
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const imageScaleX =
    layout.fitRect.width / Math.max(0.0001, layout.imageWidth);
  const imageScaleY =
    layout.fitRect.height / Math.max(0.0001, layout.imageHeight);
  const anchor = getViewportLayoutAnchor(layout);

  return {
    scale: safeScale,
    offsetX:
      layout.containerWidth * 0.5 -
      layout.fitRect.left -
      anchor.x * imageScaleX * safeScale,
    offsetY:
      layout.containerHeight * 0.5 -
      layout.fitRect.top -
      anchor.y * imageScaleY * safeScale,
  };
}

function rebaseViewportTransformForLayoutChange({
  previousLayout,
  nextLayout,
  transform,
}: {
  previousLayout: ViewportLayoutSnapshot;
  nextLayout: ViewportLayoutSnapshot;
  transform: ViewportTransform;
}): ViewportTransform {
  const scale = Number.isFinite(transform.scale) && transform.scale > 0
    ? transform.scale
    : 1;
  const previousScaleX =
    previousLayout.fitRect.width / Math.max(0.0001, previousLayout.imageWidth);
  const previousScaleY =
    previousLayout.fitRect.height / Math.max(0.0001, previousLayout.imageHeight);
  const nextScaleX =
    nextLayout.fitRect.width / Math.max(0.0001, nextLayout.imageWidth);
  const nextScaleY =
    nextLayout.fitRect.height / Math.max(0.0001, nextLayout.imageHeight);
  const previousAnchor = getViewportLayoutAnchor(previousLayout);
  const nextAnchor = getViewportLayoutAnchor(nextLayout);

  const anchorScreenX =
    previousLayout.fitRect.left +
    transform.offsetX +
    previousAnchor.x * previousScaleX * scale;
  const anchorScreenY =
    previousLayout.fitRect.top +
    transform.offsetY +
    previousAnchor.y * previousScaleY * scale;

  return {
    scale,
    offsetX:
      anchorScreenX -
      nextLayout.fitRect.left -
      nextAnchor.x * nextScaleX * scale,
    offsetY:
      anchorScreenY -
      nextLayout.fitRect.top -
      nextAnchor.y * nextScaleY * scale,
  };
}

function areViewportTransformsEqual(
  a: ViewportTransform,
  b: ViewportTransform,
) {
  return (
    a.scale === b.scale &&
    a.offsetX === b.offsetX &&
    a.offsetY === b.offsetY
  );
}

function areWindowingPoliciesEqual(
  a: ImageWindowingPolicy | undefined,
  b: ImageWindowingPolicy | undefined,
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.windowLevel === b.windowLevel &&
    a.windowWidth === b.windowWidth &&
    a.invert === b.invert
  );
}

const liveWindowingPolicySubscribers = new Set<(
  policy: ImageWindowingPolicy,
  sourceId: string,
) => void>();

function createStackInstanceId() {
  return `image-layer-stack-${Math.random().toString(36).slice(2)}`;
}

function publishLiveWindowingPolicy(
  policy: ImageWindowingPolicy,
  sourceId: string,
) {
  liveWindowingPolicySubscribers.forEach((subscriber) => {
    subscriber(policy, sourceId);
  });
}

export function publishImageLayerStackLiveWindowingPolicy(
  policy: ImageWindowingPolicy,
  sourceId = 'external',
) {
  publishLiveWindowingPolicy(policy, sourceId);
}

function subscribeLiveWindowingPolicy(
  subscriber: (policy: ImageWindowingPolicy, sourceId: string) => void,
) {
  liveWindowingPolicySubscribers.add(subscriber);
  return () => {
    liveWindowingPolicySubscribers.delete(subscriber);
  };
}

type ResolveInputSnapshot = {
  layers: ResolvedImageLayerPaths | null;
  resetKey?: string;
  revision?: string;
  initialViewBox?: ImageViewBox;
  requestedWidth: number;
  requestedHeight: number;
  seriesId?: string;
  visibility?: Partial<Record<ImageLayerKey, boolean>>;
  priority?: number;
  localOverlayContextVersion: number;
  overlayPolicy?: OverlayPaintPolicy;
  windowingPolicy?: ImageWindowingPolicy;
  cacheScope?: ImageCacheScope;
};

function isWindowingOnlyResolveChange(
  previous: ResolveInputSnapshot | null,
  next: ResolveInputSnapshot,
) {
  if (!previous) return false;
  return (
    previous.layers === next.layers &&
    previous.resetKey === next.resetKey &&
    previous.revision === next.revision &&
    previous.initialViewBox === next.initialViewBox &&
    previous.requestedWidth === next.requestedWidth &&
    previous.requestedHeight === next.requestedHeight &&
    previous.seriesId === next.seriesId &&
    previous.visibility === next.visibility &&
    previous.priority === next.priority &&
    previous.localOverlayContextVersion === next.localOverlayContextVersion &&
    previous.overlayPolicy === next.overlayPolicy &&
    previous.cacheScope === next.cacheScope &&
    !areWindowingPoliciesEqual(previous.windowingPolicy, next.windowingPolicy)
  );
}

function isResolvedCurrentLayer(
  state: ImageLayerState | undefined,
  expectedSource: string | undefined,
  { requireSrc = true }: { requireSrc?: boolean } = {},
) {
  if (!state || !expectedSource) return false;
  if (state.source !== expectedSource) return false;
  if (state.stale) return false;
  if (state.status === "resolved") return !requireSrc || !!state.src;
  if (state.status === "transparent" || state.status === "failed") return true;
  return false;
}

function isLayerCompositeReady(
  layers: ResolvedImageLayerPaths | null,
  layerStates: ImageLayerState[],
  visibility?: Partial<Record<ImageLayerKey, boolean>>,
) {
  if (!layers?.base) return false;

  const stateByKey = new Map<ImageLayerKey, ImageLayerState>();
  layerStates.forEach((state) => stateByKey.set(state.key, state));

  if (!isResolvedCurrentLayer(stateByKey.get("base"), layers.base)) {
    return false;
  }

  return IMAGE_LAYER_RENDER_ORDER.every((key) => {
    if (key === "base") return true;
    const source = layers[key];
    if (!source || visibility?.[key] === false) return true;
    return isResolvedCurrentLayer(stateByKey.get(key), source, {
      requireSrc: false,
    });
  });
}

export default function ImageLayerStack({
  layers,
  revision,
  width,
  height,
  seriesId,
  accessToken,
  prefetchLayers,
  prefetchConcurrency,
  className = "",
  fitMode = "contain",
  initialViewBox,
  viewportAnchorPoint,
  resetKey,
  enableViewportTransform = false,
  activeToolId,
  visibility,
  overlayPolicy,
  windowingPolicy,
  cacheScope,
  diagnosticLabel,
  priority,
  loading = false,
  disableStaleFallback = false,
  onWheel,
  onScrollDrag,
  onWindowLevelChange,
  viewportOverlay,
  children,
}: ImageLayerStackProps) {
  const stackRef = useRef<HTMLDivElement>(null);
  const stackInstanceIdRef = useRef(createStackInstanceId());
  const previousResolveInputRef = useRef<ResolveInputSnapshot | null>(null);
  const windowingPolicyRef = useRef<ImageWindowingPolicy | undefined>(
    windowingPolicy,
  );
  const dragStateRef = useRef<{
    kind: DragInteractionKind;
    clientX: number;
    clientY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const pendingWindowLevelRef = useRef<ImageWindowingPolicy | null>(null);
  const windowLevelAnimationFrameRef = useRef<number | null>(null);
  const overlayResolveTimerRef = useRef<number | null>(null);
  const prefetchTimerRef = useRef<number | null>(null);
  const lastWheelAtRef = useRef(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [layerStates, setLayerStates] = useState<ImageLayerState[]>([]);
  const [committedLayerEntries, setCommittedLayerEntries] = useState<
    DisplayLayerEntry[]
  >([]);
  const committedViewportOverlayRef = useRef<React.ReactNode>(null);
  const lastResetKeyRef = useRef(resetKey);
  const viewportLayoutSnapshotRef = useRef<ViewportLayoutSnapshot | null>(null);
  const hasViewportTransformInteractionRef = useRef(false);
  const dimensionMismatchLogRef = useRef<Set<string>>(new Set());
  const resolveRequestSeqRef = useRef(0);
  const [localOverlayContextVersion, setLocalOverlayContextVersion] = useState(
    () => getLocalOverlayImageContextVersion(seriesId),
  );
  const [prefetchWindowingPolicy, setPrefetchWindowingPolicy] = useState<
    ImageWindowingPolicy | undefined
  >(windowingPolicy);
  const [interactionWindowingPolicy, setInteractionWindowingPolicy] = useState<
    ImageWindowingPolicy | undefined
  >(undefined);
  const interactionWindowingPolicyRef = useRef<ImageWindowingPolicy | undefined>(
    undefined,
  );
  const requestedWidth = Number.isFinite(width) && width > 0 ? width : 1;
  const requestedHeight = Number.isFinite(height) && height > 0 ? height : 1;
  const [displayGeometry, setDisplayGeometry] = useState<DisplayGeometry>({
    width: requestedWidth,
    height: requestedHeight,
    initialViewBox,
    viewportAnchorPoint: isFiniteImagePoint(viewportAnchorPoint)
      ? viewportAnchorPoint
      : undefined,
  });
  const displayGeometryRef = useRef(displayGeometry);
  const committedLayerEntriesRef = useRef(committedLayerEntries);
  displayGeometryRef.current = displayGeometry;
  committedLayerEntriesRef.current = committedLayerEntries;
  const [viewportTransform, setViewportTransform] = useState<ViewportTransform>(
    {
      scale: 1,
      offsetX: 0,
      offsetY: 0,
    },
  );
  const safeWidth = displayGeometry.width;
  const safeHeight = displayGeometry.height;
  const effectiveContainerWidth = containerSize.width || safeWidth;
  const effectiveContainerHeight = containerSize.height || safeHeight;
  const effectiveWindowingPolicy = interactionWindowingPolicy ?? windowingPolicy;
  windowingPolicyRef.current = effectiveWindowingPolicy;
  interactionWindowingPolicyRef.current = interactionWindowingPolicy;
  const fitRect = calculateImageFitRect(
    fitMode,
    effectiveContainerWidth,
    effectiveContainerHeight,
    safeWidth,
    safeHeight,
    displayGeometry.initialViewBox,
  );
  const stageRect = useMemo(
    () => ({
      left: fitRect.left + viewportTransform.offsetX,
      top: fitRect.top + viewportTransform.offsetY,
      width: fitRect.width * viewportTransform.scale,
      height: fitRect.height * viewportTransform.scale,
    }),
    [
      fitRect.height,
      fitRect.left,
      fitRect.top,
      fitRect.width,
      viewportTransform.offsetX,
      viewportTransform.offsetY,
      viewportTransform.scale,
    ],
  );
  const viewportContext = useMemo<ImageLayerStackViewportContext>(() => {
    const scaleX = stageRect.width / safeWidth;
    const scaleY = stageRect.height / safeHeight;
    return {
      imageWidth: safeWidth,
      imageHeight: safeHeight,
      containerWidth: effectiveContainerWidth,
      containerHeight: effectiveContainerHeight,
      fitRect,
      viewportTransform,
      stageRect,
      scaleX,
      scaleY,
      imageToViewportPoint: ({ x, y }) => ({
        x: stageRect.left + x * scaleX,
        y: stageRect.top + y * scaleY,
      }),
      viewportToImagePoint: ({ x, y }) => ({
        x: (x - stageRect.left) / Math.max(0.0001, scaleX),
        y: (y - stageRect.top) / Math.max(0.0001, scaleY),
      }),
    };
  }, [
    effectiveContainerHeight,
    effectiveContainerWidth,
    fitRect,
    safeHeight,
    safeWidth,
    stageRect,
    viewportTransform,
  ]);

  useLayoutEffect(() => {
    const nextSnapshot: ViewportLayoutSnapshot = {
      resetKey,
      revision,
      fitMode,
      containerWidth: effectiveContainerWidth,
      containerHeight: effectiveContainerHeight,
      imageWidth: safeWidth,
      imageHeight: safeHeight,
      initialViewBox: displayGeometry.initialViewBox,
      viewportAnchorPoint: isFiniteImagePoint(displayGeometry.viewportAnchorPoint)
        ? displayGeometry.viewportAnchorPoint
        : undefined,
      fitRect,
    };
    const previousSnapshot = viewportLayoutSnapshotRef.current;

    if (
      enableViewportTransform &&
      previousSnapshot &&
      previousSnapshot.resetKey === resetKey &&
      previousSnapshot.revision === revision &&
      previousSnapshot.fitMode === fitMode &&
      !areViewportLayoutSnapshotsEqual(previousSnapshot, nextSnapshot)
    ) {
      setViewportTransform((previousTransform) => {
        const nextTransform = hasViewportTransformInteractionRef.current
          ? rebaseViewportTransformForLayoutChange({
              previousLayout: previousSnapshot,
              nextLayout: nextSnapshot,
              transform: previousTransform,
            })
          : isFiniteImagePoint(nextSnapshot.viewportAnchorPoint)
            ? calculateAnchorCenteredViewportTransform({
                layout: nextSnapshot,
                scale: 1,
              })
            : previousTransform;
        return areViewportTransformsEqual(previousTransform, nextTransform)
          ? previousTransform
          : nextTransform;
      });
    } else if (
      enableViewportTransform &&
      !previousSnapshot &&
      isFiniteImagePoint(nextSnapshot.viewportAnchorPoint)
    ) {
      setViewportTransform((previousTransform) => {
        const nextTransform = calculateAnchorCenteredViewportTransform({
          layout: nextSnapshot,
          scale: 1,
        });
        return areViewportTransformsEqual(previousTransform, nextTransform)
          ? previousTransform
          : nextTransform;
      });
    }

    viewportLayoutSnapshotRef.current = nextSnapshot;
  }, [
    displayGeometry.initialViewBox,
    effectiveContainerHeight,
    effectiveContainerWidth,
    enableViewportTransform,
    fitMode,
    fitRect.height,
    fitRect.left,
    fitRect.top,
    fitRect.width,
    resetKey,
    revision,
    safeHeight,
    safeWidth,
    displayGeometry.viewportAnchorPoint,
  ]);

  useLayoutEffect(() => {
    if (lastResetKeyRef.current === resetKey) return;

    lastResetKeyRef.current = resetKey;
    hasViewportTransformInteractionRef.current = false;
    committedViewportOverlayRef.current = null;
    committedLayerEntriesRef.current = [];

    setLayerStates((previousStates) =>
      previousStates.length === 0 ? previousStates : [],
    );
    setCommittedLayerEntries((previousEntries) =>
      previousEntries.length === 0 ? previousEntries : [],
    );
  }, [resetKey]);

  useEffect(() => {
    return () => {
      if (windowLevelAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(windowLevelAnimationFrameRef.current);
        windowLevelAnimationFrameRef.current = null;
      }
      if (overlayResolveTimerRef.current !== null) {
        window.clearTimeout(overlayResolveTimerRef.current);
        overlayResolveTimerRef.current = null;
      }
      if (prefetchTimerRef.current !== null) {
        window.clearTimeout(prefetchTimerRef.current);
        prefetchTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const node = stackRef.current;
    if (!node) return;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      const nextSize = {
        width: Math.max(1, rect.width),
        height: Math.max(1, rect.height),
      };
      setContainerSize((previousSize) =>
        previousSize.width === nextSize.width &&
        previousSize.height === nextSize.height
          ? previousSize
          : nextSize,
      );
    };
    updateSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    clearServerImageCacheExceptRevision(revision);
  }, [revision]);

  useEffect(() => retainServerImageCacheScope(cacheScope), [cacheScope]);

  useEffect(() => {
    setLocalOverlayContextVersion(getLocalOverlayImageContextVersion(seriesId));
    return subscribeLocalOverlayImageContextVersion(
      seriesId,
      setLocalOverlayContextVersion,
    );
  }, [seriesId]);

  useEffect(
    () =>
      subscribeLiveWindowingPolicy((policy, sourceId) => {
        if (sourceId === stackInstanceIdRef.current) return;
        const basePolicy = windowingPolicyRef.current ?? windowingPolicy;
        if (!basePolicy && !policy) return;
        const nextPolicy = {
          ...(basePolicy ?? policy),
          ...policy,
          invert: policy.invert ?? basePolicy?.invert,
        };
        if (areWindowingPoliciesEqual(interactionWindowingPolicyRef.current, nextPolicy)) {
          return;
        }
        windowingPolicyRef.current = nextPolicy;
        interactionWindowingPolicyRef.current = nextPolicy;
        setInteractionWindowingPolicy((previousPolicy) =>
          areWindowingPoliciesEqual(previousPolicy, nextPolicy)
            ? previousPolicy
            : nextPolicy,
        );
      }),
    [windowingPolicy],
  );

  useEffect(() => {
    const interactionPolicy = interactionWindowingPolicyRef.current;
    if (!interactionPolicy) {
      windowingPolicyRef.current = windowingPolicy;
      return;
    }
    if (dragStateRef.current?.kind === "window-level") return;

    // Clear the CPR-only live preview policy whenever the committed/global VOI
    // changes.  If the global policy caught up to a CPR drag, this simply
    // removes the temporary preview.  If the change came from another view
    // (axial/cross section), this prevents the previous CPR preview value from
    // continuing to override the new global W/L.
    setInteractionWindowingPolicy(undefined);
    interactionWindowingPolicyRef.current = undefined;
    windowingPolicyRef.current = windowingPolicy;
  }, [windowingPolicy]);

  useEffect(() => {
    if (!effectiveWindowingPolicy) {
      setPrefetchWindowingPolicy((previousPolicy) =>
        previousPolicy === undefined ? previousPolicy : undefined,
      );
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setPrefetchWindowingPolicy((previousPolicy) =>
        areWindowingPoliciesEqual(previousPolicy, effectiveWindowingPolicy)
          ? previousPolicy
          : effectiveWindowingPolicy,
      );
    }, 120);

    return () => window.clearTimeout(timer);
  }, [effectiveWindowingPolicy]);

  useLayoutEffect(() => {
    hasViewportTransformInteractionRef.current = false;
    interactionWindowingPolicyRef.current = undefined;
    setInteractionWindowingPolicy(undefined);
    const resetLayout: ViewportLayoutSnapshot = {
      resetKey,
      revision,
      fitMode,
      containerWidth: effectiveContainerWidth,
      containerHeight: effectiveContainerHeight,
      imageWidth: safeWidth,
      imageHeight: safeHeight,
      initialViewBox: displayGeometry.initialViewBox,
      viewportAnchorPoint: isFiniteImagePoint(displayGeometry.viewportAnchorPoint)
        ? displayGeometry.viewportAnchorPoint
        : undefined,
      fitRect,
    };
    const resetTransform: ViewportTransform = isFiniteImagePoint(
      resetLayout.viewportAnchorPoint,
    )
      ? calculateAnchorCenteredViewportTransform({
          layout: resetLayout,
          scale: 1,
        })
      : { scale: 1, offsetX: 0, offsetY: 0 };
    setViewportTransform((previousTransform) =>
      areViewportTransformsEqual(previousTransform, resetTransform)
        ? previousTransform
        : resetTransform,
    );
  }, [fitMode, resetKey, revision]);

  useEffect(() => {
    if (layers?.base) return;
    // When a branch is selected for the first time, its manifest may still be
    // loading. Keep the last committed CPR frame geometry instead of stretching
    // the previous image to a temporary container-sized fallback.
    if (committedLayerEntries.length > 0) return;
    const nextGeometry: DisplayGeometry = {
      width: requestedWidth,
      height: requestedHeight,
      initialViewBox,
      viewportAnchorPoint: isFiniteImagePoint(viewportAnchorPoint)
        ? viewportAnchorPoint
        : undefined,
    };
    if (!areDisplayGeometriesEqual(displayGeometryRef.current, nextGeometry)) {
      displayGeometryRef.current = nextGeometry;
      setDisplayGeometry(nextGeometry);
    }
  }, [
    committedLayerEntries.length,
    initialViewBox,
    layers?.base,
    requestedHeight,
    requestedWidth,
    viewportAnchorPoint,
  ]);

  useEffect(() => {
    dimensionMismatchLogRef.current.clear();
  }, [layers, safeHeight, safeWidth]);

  useEffect(() => {
    let cancelled = false;
    const requestSeq = ++resolveRequestSeqRef.current;
    const initialStates = createInitialLayerStates(layers, visibility);
    const statesToResolve = initialStates.filter(
      (state) => state.status === "pending" && !!state.source,
    );
    const resolveInputSnapshot: ResolveInputSnapshot = {
      layers,
      resetKey,
      revision,
      initialViewBox,
      requestedWidth,
      requestedHeight,
      seriesId,
      visibility,
      priority,
      localOverlayContextVersion,
      overlayPolicy,
      windowingPolicy: effectiveWindowingPolicy,
      cacheScope,
    };
    const isWindowingOnlyChange = isWindowingOnlyResolveChange(
      previousResolveInputRef.current,
      resolveInputSnapshot,
    );
    previousResolveInputRef.current = resolveInputSnapshot;
    const isCurrentRequest = () =>
      !cancelled && resolveRequestSeqRef.current === requestSeq;

    setLayerStates((previousStates) => {
      if (!initialStates.length) {
        return previousStates.length === 0 ? previousStates : initialStates;
      }

      const previousResolvedByKey = new Map<ImageLayerKey, ImageLayerState>();
      previousStates.forEach((state) => {
        if (state.status === "resolved" && state.src) {
          previousResolvedByKey.set(state.key, state);
        }
      });

      const nextStates = initialStates.map((state) => {
        if (state.status !== "pending" || !state.source) return state;
        const previousResolved = previousResolvedByKey.get(state.key);
        if (
          isWindowingOnlyChange &&
          state.key !== "base" &&
          previousResolved?.source === state.source
        ) {
          return {
            ...state,
            status: "resolved",
            src: previousResolved.src,
            stale: false,
            staleSource: undefined,
          };
        }
        if (!canCarryStaleLayer(previousResolved, state)) return state;
        return {
          ...state,
          status: "resolved",
          src: previousResolved!.src,
          stale: true,
          staleSource: previousResolved!.source,
        };
      });
      return areLayerStatesEqual(previousStates, nextStates)
        ? previousStates
        : nextStates;
    });

    if (!layers?.base) {
      return () => {
        cancelled = true;
      };
    }

    const startResolve = () => {
      if (cancelled) return;
      const stackResolveStart = stackPerfNow();
      logStackPerf("stack-resolve-start", {
        requestSeq,
        diagnosticLabel,
        layerCount: statesToResolve.length,
        width: requestedWidth,
        height: requestedHeight,
      });
      const resolveState = async (
        state: ImageLayerState,
        attempt = 0,
      ): Promise<{
        key: ImageLayerKey;
        source: string;
        status: ImageLayerResolveStatus;
        src?: string;
        error?: string;
      }> => {
        const source = state.source!;
        const layerStart = stackPerfNow();
        try {
          const resolved = await resolveImageSourceQueued(source, {
            seriesId,
            accessToken,
            revision,
            width: requestedWidth,
            height: requestedHeight,
            visibility,
            priority,
            localOverlayContextVersion,
            overlayPolicy,
            windowingPolicy: effectiveWindowingPolicy,
            cacheScope,
            shouldCancel: () => !isCurrentRequest(),
          });
          logStackPerf("stack-layer-resolved", {
            requestSeq,
            diagnosticLabel,
            layer: state.key,
            staleCancelled: !isCurrentRequest(),
            elapsedMs: stackRoundMs(stackPerfNow() - layerStart),
            source: source.slice(0, 180),
            resolved: resolved.slice(0, 180),
          });
          return {
            key: state.key,
            source,
            status: isUnresolvedOverlayPseudoPath(resolved)
              ? "transparent"
              : "resolved",
            src: isUnresolvedOverlayPseudoPath(resolved) ? undefined : resolved,
          } as const;
        } catch (err) {
          const retryDelay = CPR_LAYER_RESOLVE_RETRY_DELAYS_MS[attempt];
          logStackPerf("stack-layer-failed", {
            requestSeq,
            diagnosticLabel,
            layer: state.key,
            attempt,
            retryDelay,
            staleCancelled: !isCurrentRequest(),
            elapsedMs: stackRoundMs(stackPerfNow() - layerStart),
            source: source.slice(0, 180),
            error: err instanceof Error ? err.message : String(err),
          });
          if (retryDelay !== undefined && isCurrentRequest()) {
            await new Promise((resolve) => window.setTimeout(resolve, retryDelay));
            if (isCurrentRequest()) {
              return resolveState(state, attempt + 1);
            }
          }
          return {
            key: state.key,
            source,
            status: "failed",
            src: undefined,
            error: err instanceof Error ? err.message : String(err),
          };
        }
      };

      const baseStates = statesToResolve.filter((state) => state.key === "base");
      const overlayStates = statesToResolve.filter((state) => state.key !== "base");

      if (overlayResolveTimerRef.current !== null) {
        window.clearTimeout(overlayResolveTimerRef.current);
        overlayResolveTimerRef.current = null;
      }

      // Resolve the whole CPR frame together and update React state once.  The
      // visible composite already waits for all current layers, so individual
      // per-layer setState calls only add extra render work during fast angle
      // changes.
      void Promise.allSettled([...baseStates, ...overlayStates].map(resolveState)).then(
        (settled) => {
          if (!isCurrentRequest()) return;
          const updates = settled.flatMap((result) =>
            result.status === "fulfilled" ? [result.value] : [],
          );
          if (!updates.length) return;
          setLayerStates((prev) => {
            const nextStates = prev.map((prevState) => {
              const update = updates.find(
                (entry) =>
                  entry.key === prevState.key && entry.source === prevState.source,
              );
              if (!update) return prevState;
              return {
                ...prevState,
                status: update.status,
                src: update.src,
                stale: false,
                staleSource: undefined,
                error: update.error,
              };
            });
            return areLayerStatesEqual(prev, nextStates) ? prev : nextStates;
          });
        },
      );
    };

    // Resolve immediately instead of waiting for another RAF tick.  CPR wheel
    // input is already discrete by angle frame; adding a second RAF here made
    // hot-cache transitions feel one frame behind the cursor.
    startResolve();

    return () => {
      cancelled = true;
      if (overlayResolveTimerRef.current !== null) {
        window.clearTimeout(overlayResolveTimerRef.current);
        overlayResolveTimerRef.current = null;
      }
    };
  }, [
    accessToken,
    layers,
    resetKey,
    revision,
    initialViewBox,
    requestedHeight,
    requestedWidth,
    seriesId,
    visibility,
    priority,
    localOverlayContextVersion,
    overlayPolicy,
    effectiveWindowingPolicy,
    cacheScope,
  ]);

  useEffect(() => {
    if (prefetchTimerRef.current !== null) {
      window.clearTimeout(prefetchTimerRef.current);
      prefetchTimerRef.current = null;
    }

    const prefetchDelay =
      Date.now() - lastWheelAtRef.current < CPR_WHEEL_ACTIVE_MS ? 32 : CPR_PREFETCH_IDLE_MS;

    prefetchTimerRef.current = window.setTimeout(() => {
      prefetchTimerRef.current = null;
      const isWheelActive = Date.now() - lastWheelAtRef.current < CPR_WHEEL_ACTIVE_MS;
      const frameLimit = isWheelActive
        ? Math.max(2, Math.min(4, prefetchConcurrency ?? 2))
        : Math.min(prefetchLayers?.length ?? 0, CPR_PREFETCH_FRAME_LIMIT);
      const pathLimit = isWheelActive
        ? Math.min(24, CPR_PREFETCH_PATH_LIMIT)
        : Math.min(
            CPR_PREFETCH_PATH_LIMIT,
            Math.max(
              CPR_PREFETCH_PATH_LIMIT,
              (prefetchLayers?.length ?? 0) * IMAGE_LAYER_RENDER_ORDER.length,
            ),
          );
      const prefetchPaths: string[] = [];
      const seenPrefetchPaths = new Set<string>();

      (prefetchLayers ?? []).slice(0, frameLimit).forEach((layerSet) => {
        if (!layerSet) return;
        IMAGE_LAYER_RENDER_ORDER.forEach((key) => {
          const path = layerSet[key];
          if (!path || (key !== "base" && visibility?.[key] === false)) return;
          // Keep the full CPR frame hot.  The displayed composite now waits for
          // base + raster overlays, so skipping label/outline prefetch makes the
          // next angle pay the 5-11ms zip decode cost on the wheel path.
          if (seenPrefetchPaths.has(path) || prefetchPaths.length >= pathLimit) return;
          seenPrefetchPaths.add(path);
          prefetchPaths.push(path);
        });
      });

      if (!prefetchPaths.length) return;

      prefetchImageSourcesQueued(prefetchPaths, {
        seriesId,
        accessToken,
        revision,
        width: requestedWidth,
        height: requestedHeight,
        priority: 260,
        maxConcurrent: isWheelActive
          ? 1
          : Math.min(3, Math.max(2, prefetchConcurrency ?? 2)),
        replaceQueuedForScope: isWheelActive,
        localOverlayContextVersion,
        overlayPolicy,
        windowingPolicy: prefetchWindowingPolicy,
        cacheScope,
      });
    }, prefetchDelay);

    return () => {
      if (prefetchTimerRef.current !== null) {
        window.clearTimeout(prefetchTimerRef.current);
        prefetchTimerRef.current = null;
      }
    };
  }, [
    accessToken,
    prefetchLayers,
    prefetchConcurrency,
    revision,
    requestedHeight,
    requestedWidth,
    seriesId,
    visibility,
    localOverlayContextVersion,
    overlayPolicy,
    prefetchWindowingPolicy,
    cacheScope,
  ]);

  const currentCompositeReady = useMemo(
    () => isLayerCompositeReady(layers, layerStates, visibility),
    [layerStates, layers, visibility],
  );
  const currentFreshEntries = useMemo(
    () => makeDisplayEntriesFromLayerStates(layerStates),
    [layerStates],
  );
  const transitionalEntries = useMemo(() => {
    const baseState = layerStates.find((state) => state.key === "base");
    if (baseState?.stale) {
      return makeDisplayEntriesFromLayerStates(layerStates, { includeStale: true });
    }
    return currentFreshEntries;
  }, [currentFreshEntries, layerStates]);

  useLayoutEffect(() => {
    if (!currentCompositeReady) return;
    const commitStart = stackPerfNow();

    const nextGeometry: DisplayGeometry = {
      width: requestedWidth,
      height: requestedHeight,
      initialViewBox,
      viewportAnchorPoint: isFiniteImagePoint(viewportAnchorPoint)
        ? viewportAnchorPoint
        : undefined,
    };
    if (!areDisplayGeometriesEqual(displayGeometryRef.current, nextGeometry)) {
      displayGeometryRef.current = nextGeometry;
      setDisplayGeometry(nextGeometry);
    }

    const entriesChanged = !areDisplayEntriesEqual(committedLayerEntriesRef.current, currentFreshEntries);
    if (entriesChanged) {
      committedLayerEntriesRef.current = currentFreshEntries;
      setCommittedLayerEntries(currentFreshEntries);
    }
  }, [
    cacheScope,
    currentCompositeReady,
    currentFreshEntries,
    diagnosticLabel,
    initialViewBox,
    requestedHeight,
    requestedWidth,
    viewportAnchorPoint,
  ]);

  const canUseCommittedEntries = useMemo(() => {
    const nextBaseContinuityKey = sourceContinuityKey(layers?.base);
    if (!nextBaseContinuityKey) return false;
    const committedBase = committedLayerEntries.find(
      (entry) => entry.key === "base",
    );
    if (!committedBase?.source) return false;
    return (
      sourceContinuityKey(committedBase.source) === nextBaseContinuityKey
    );
  }, [committedLayerEntries, layers?.base]);

  const entries = useMemo(() => {
    if (loading) return [];

    if (disableStaleFallback) {
      return currentCompositeReady
        ? currentFreshEntries.map((entry) => [entry.key, entry.src] as const)
        : [];
    }

    const selectedEntries = currentCompositeReady
      ? currentFreshEntries
      : !layers?.base && committedLayerEntries.length > 0
        ? committedLayerEntries
        : canUseCommittedEntries && committedLayerEntries.length > 0
          ? committedLayerEntries
          : transitionalEntries;

    return selectedEntries.map((entry) => [entry.key, entry.src] as const);
  }, [
    canUseCommittedEntries,
    committedLayerEntries,
    currentCompositeReady,
    currentFreshEntries,
    disableStaleFallback,
    layers?.base,
    loading,
    transitionalEntries,
  ]);
  const isWheelInteractionActive = Date.now() - lastWheelAtRef.current < CPR_WHEEL_ACTIVE_MS;
  const shouldUseSingleMemoryComposite = useMemo(
    () => !isWheelInteractionActive && canUseMemoryComposite(entries),
    [entries, isWheelInteractionActive],
  );
  const currentViewportOverlayNode = viewportOverlay
    ? viewportOverlay(viewportContext)
    : null;

  if (!viewportOverlay) {
    committedViewportOverlayRef.current = null;
  } else if (currentCompositeReady) {
    committedViewportOverlayRef.current = currentViewportOverlayNode;
  }

  const viewportOverlayNode = currentCompositeReady
    ? currentViewportOverlayNode
    : disableStaleFallback || loading
      ? null
      : committedViewportOverlayRef.current;
  const viewportOverlaySyncState = !viewportOverlay
    ? "ready"
    : currentCompositeReady
      ? "ready"
      : viewportOverlayNode
        ? "committed"
        : "waiting";
  const layerStateDiagnostic = useMemo(
    () => layerStates.map((state) => `${state.key}:${state.status}`).join(","),
    [layerStates],
  );
  const hasPendingLayer = layerStates.some(
    (state) => state.status === "pending",
  );
  const hasDisplayBaseLayer = entries.some(([key]) => key === "base");
  const shouldShowLoadingMessage =
    loading || hasPendingLayer || (disableStaleFallback && !!layers?.base);
  const windowingDiagnostic = useMemo(
    () =>
      effectiveWindowingPolicy
        ? `wl=${effectiveWindowingPolicy.windowLevel},ww=${effectiveWindowingPolicy.windowWidth},invert=${effectiveWindowingPolicy.invert === true}`
        : undefined,
    [effectiveWindowingPolicy],
  );

  const handleLayerLoad = useCallback(
    (_key: ImageLayerKey, _event: React.SyntheticEvent<HTMLImageElement>) => {
      // Dimension mismatch is expected while server CPR angle frames with
      // different sizes are being swapped. Keep this off the UI and console;
    },
    [],
  );

  const handleMemoryLayerRendered = useCallback(
    (_key: ImageLayerKey, _src: string, _naturalWidth: number, _naturalHeight: number) => {
      // See handleLayerLoad above.
    },
    [],
  );

  const handleStackWheel = useCallback((event: ImageLayerStackWheelEvent) => {
    if (enableViewportTransform && event.ctrlKey) {
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
      const stackRect = stackRef.current?.getBoundingClientRect();
      if (!stackRect) return;
      const localX = event.clientX - stackRect.left;
      const localY = event.clientY - stackRect.top;
      hasViewportTransformInteractionRef.current = true;
      setViewportTransform((prev) =>
        calculateZoomedViewportTransform({
          previous: prev,
          fitRect,
          localX,
          localY,
          scaleFactor: event.deltaY > 0 ? 0.9 : 1.1,
        }),
      );
      return;
    }

    onWheel?.(event);
  }, [enableViewportTransform, fitRect, onWheel]);

  useEffect(() => {
    const element = stackRef.current;
    if (!element) return;

    const handleNativeWheel = (event: WheelEvent) => {
      lastWheelAtRef.current = Date.now();
      handleStackWheel(event);
    };

    element.addEventListener("wheel", handleNativeWheel, { passive: false });
    return () => {
      element.removeEventListener("wheel", handleNativeWheel);
    };
  }, [handleStackWheel]);

  const resolveDragInteraction = (
    event: React.PointerEvent<HTMLDivElement>,
  ): DragInteractionKind | null => {
    if (!enableViewportTransform) return null;
    if (event.buttons === 3) return "zoom";
    if (event.button === 1 || event.buttons === 4) {
      return onWindowLevelChange && windowingPolicy ? "window-level" : "pan";
    }
    if (event.button === 2) return "pan";
    if (event.button !== 0) return null;
    if (
      activeToolId === "WindowLevel" &&
      onWindowLevelChange &&
      windowingPolicy
    ) {
      return "window-level";
    }
    if (activeToolId === "Pan") return "pan";
    if (activeToolId === "Zoom") return "zoom";
    if (activeToolId === "Scroll" && onScrollDrag) return "scroll";
    return null;
  };

  const zoomAtClientPoint = useCallback(
    (clientX: number, clientY: number, scaleFactor: number) => {
      const stackRect = stackRef.current?.getBoundingClientRect();
      if (!stackRect) return;
      const localX = clientX - stackRect.left;
      const localY = clientY - stackRect.top;
      hasViewportTransformInteractionRef.current = true;
      setViewportTransform((prev) =>
        calculateZoomedViewportTransform({
          previous: prev,
          fitRect,
          localX,
          localY,
          scaleFactor,
        }),
      );
    },
    [fitRect],
  );

  const handleViewportPointerDown: React.PointerEventHandler<HTMLDivElement> = (
    event,
  ) => {
    if (!enableViewportTransform) return;
    const dragKind = resolveDragInteraction(event);
    if (!dragKind) return;
    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current = {
      kind: dragKind,
      clientX: event.clientX,
      clientY: event.clientY,
      offsetX: viewportTransform.offsetX,
      offsetY: viewportTransform.offsetY,
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      const deltaX = moveEvent.clientX - drag.clientX;
      const deltaY = moveEvent.clientY - drag.clientY;

      if (drag.kind === "pan") {
        if (deltaX !== 0 || deltaY !== 0) {
          hasViewportTransformInteractionRef.current = true;
        }
        setViewportTransform((prev) => ({
          ...prev,
          offsetX: drag.offsetX + deltaX,
          offsetY: drag.offsetY + deltaY,
        }));
        return;
      }

      if (drag.kind === "zoom") {
        if (deltaY !== 0) {
          zoomAtClientPoint(
            moveEvent.clientX,
            moveEvent.clientY,
            deltaY > 0 ? 0.98 : 1.02,
          );
        }
        drag.clientX = moveEvent.clientX;
        drag.clientY = moveEvent.clientY;
        return;
      }

      if (drag.kind === "scroll") {
        lastWheelAtRef.current = Date.now();
        onScrollDrag?.(deltaX, deltaY);
        drag.clientX = moveEvent.clientX;
        drag.clientY = moveEvent.clientY;
        return;
      }

      if (drag.kind === "window-level") {
        const next = calculateWindowLevelWidthFromDrag({
          current: windowingPolicyRef.current,
          deltaX,
          deltaY,
        });
        if (next) {
          windowingPolicyRef.current = next;
          interactionWindowingPolicyRef.current = next;
          setInteractionWindowingPolicy((previousPolicy) =>
            areWindowingPoliciesEqual(previousPolicy, next) ? previousPolicy : next,
          );
          publishLiveWindowingPolicy(next, stackInstanceIdRef.current);
          pendingWindowLevelRef.current = next;
          if (windowLevelAnimationFrameRef.current === null) {
            windowLevelAnimationFrameRef.current = window.requestAnimationFrame(
              () => {
                windowLevelAnimationFrameRef.current = null;
                const pendingPolicy = pendingWindowLevelRef.current;
                pendingWindowLevelRef.current = null;
                if (pendingPolicy) {
                  onWindowLevelChange?.(pendingPolicy);
                }
              },
            );
          }
        }
        drag.clientX = moveEvent.clientX;
        drag.clientY = moveEvent.clientY;
      }
    };
    const handlePointerUp = () => {
      const pendingPolicy = pendingWindowLevelRef.current;
      pendingWindowLevelRef.current = null;
      if (windowLevelAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(windowLevelAnimationFrameRef.current);
        windowLevelAnimationFrameRef.current = null;
      }
      if (pendingPolicy) {
        windowingPolicyRef.current = pendingPolicy;
        interactionWindowingPolicyRef.current = pendingPolicy;
        setInteractionWindowingPolicy((previousPolicy) =>
          areWindowingPoliciesEqual(previousPolicy, pendingPolicy)
            ? previousPolicy
            : pendingPolicy,
        );
        publishLiveWindowingPolicy(pendingPolicy, stackInstanceIdRef.current);
        onWindowLevelChange?.(pendingPolicy);
      }
      dragStateRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  };

  if ((loading || !layers) && !hasDisplayBaseLayer) {
    return (
      <div
        ref={stackRef}
        className={`server-image-stack ${className}`}
        data-testid="server-image-stack"
        data-layer-count="0"
        data-layer-states={layerStateDiagnostic}
        data-windowing={windowingDiagnostic}
        data-cache-scope={cacheScope ? JSON.stringify(cacheScope) : undefined}
        data-image-diagnostic={diagnosticLabel}
      >
        <div className="server-image-error" data-revision={revision}>
          {shouldShowLoadingMessage
            ? "Image resource loading"
            : "Image resource unavailable"}
        </div>
      </div>
    );
  }

  if (!hasDisplayBaseLayer) {
    return (
      <div
        ref={stackRef}
        className={`server-image-stack ${className}`}
        data-testid="server-image-stack"
        data-layer-count={entries.length}
        data-layer-states={layerStateDiagnostic}
        data-windowing={windowingDiagnostic}
        data-cache-scope={cacheScope ? JSON.stringify(cacheScope) : undefined}
        data-image-diagnostic={diagnosticLabel}
      >
        <div className="server-image-error" data-revision={revision}>
          {shouldShowLoadingMessage
            ? "Image resource loading"
            : "Image resource unavailable"}
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div
        ref={stackRef}
        className={`server-image-stack ${className}`}
        data-windowing={windowingDiagnostic}
        data-cache-scope={cacheScope ? JSON.stringify(cacheScope) : undefined}
        data-image-diagnostic={diagnosticLabel}
      >
        <div className="server-image-error" data-revision={revision}>
          Image resource unavailable
        </div>
      </div>
    );
  }

  return (
    <div
      ref={stackRef}
      className={`server-image-stack ${className}`}
      data-revision={revision}
      data-testid="server-image-stack"
      data-layer-count={entries.length}
      data-layer-states={layerStateDiagnostic}
      data-windowing={windowingDiagnostic}
      data-cache-scope={cacheScope ? JSON.stringify(cacheScope) : undefined}
      data-image-diagnostic={diagnosticLabel}
      onContextMenu={(event) => {
        if (enableViewportTransform) event.preventDefault();
      }}
      onPointerDown={handleViewportPointerDown}
    >
      <div
        className="server-image-stage"
        style={{
          left: stageRect.left,
          top: stageRect.top,
          width: stageRect.width,
          height: stageRect.height,
          transition: "none",
        }}
      >
        {shouldUseSingleMemoryComposite ? (
          <ServerMemoryCompositeCanvasLayer
            entries={entries}
            onRendered={(naturalWidth, naturalHeight, renderedSrc) =>
              handleMemoryLayerRendered(
                "base",
                renderedSrc,
                naturalWidth,
                naturalHeight,
              )
            }
          />
        ) : (
          entries.map(([key, src]) =>
            isServerMemoryImageSource(src) ? (
              <ServerMemoryCanvasLayer
                key={key}
                layerKey={key}
                src={src}
                onRendered={(naturalWidth, naturalHeight, renderedSrc) =>
                  handleMemoryLayerRendered(
                    key,
                    renderedSrc,
                    naturalWidth,
                    naturalHeight,
                  )
                }
              />
            ) : (
              <img
                key={key}
                className={`server-image-layer server-image-layer-${key}`}
                src={src}
                decoding="sync"
                loading="eager"
                data-layer-key={key}
                data-layer-status="resolved"
                alt=""
                draggable={false}
                style={{ transition: "none" }}
                onLoad={(event) => handleLayerLoad(key, event)}
              />
            ),
          )
        )}
        {children}
      </div>
      {viewportOverlay && (
        <div
          className="server-image-viewport-overlay"
          data-overlay-sync={viewportOverlaySyncState}
          style={{
            pointerEvents: viewportOverlayNode ? undefined : "none",
            transition: "none",
          }}
        >
          {viewportOverlayNode}
        </div>
      )}
    </div>
  );
}
